import React, { useState, useEffect } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, RefreshControl, Modal } from 'react-native';
import { Text, Card, Avatar, Searchbar, useTheme, ActivityIndicator, Chip, Divider, Button, Dialog, Portal, Paragraph, Snackbar } from 'react-native-paper';
import { getAllUsers, toggleUserStatus } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const UserListScreen = ({ navigation }) => {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();
      if (response.success) {
        setUsers(response.data || []);
        setFilteredUsers(response.data || []);
      } else {
        setSnackbarMessage('Kullanıcı listesi alınırken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setSnackbarMessage('Kullanıcı listesi alınırken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Arama yapma
  useEffect(() => {
    if (searchQuery) {
      const filtered = users.filter(user => 
        user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.name + ' ' + user.surname)?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchQuery, users]);

  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    fetchUsers();
  }, []);

  const confirmToggleStatus = (user) => {
    setUserToToggle(user);
    setStatusDialogVisible(true);
  };

  const handleToggleStatus = async () => {
    try {
      if (!userToToggle) return;
      
      const newStatus = !userToToggle.is_active;
      const response = await toggleUserStatus(userToToggle.id, newStatus);
      
      if (response.success) {
        // Kullanıcının durumunu güncelle
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userToToggle.id ? { ...user, is_active: newStatus } : user
          )
        );
        setSnackbarMessage(`Kullanıcı durumu ${newStatus ? 'aktif' : 'pasif'} olarak güncellendi`);
      } else {
        setSnackbarMessage('Kullanıcı durumu güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setSnackbarMessage('Kullanıcı durumu güncellenirken bir hata oluştu');
    } finally {
      setStatusDialogVisible(false);
      setUserToToggle(null);
      setSnackbarVisible(true);
    }
  };

  const navigateToUserDetail = (user) => {
    navigation.navigate('UserDetail', { userId: user.id, username: user.username });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    } catch (error) {
      return dateString;
    }
  };

  const renderUserItem = ({ item: user }) => (
    <Card style={styles.card} onPress={() => navigateToUserDetail(user)}>
      <Card.Content>
        <View style={styles.userHeader}>
          <Avatar.Text 
            size={50} 
            label={
              (user.name && user.surname) 
                ? `${user.name.charAt(0)}${user.surname.charAt(0)}` 
                : user.name 
                  ? user.name.charAt(0)
                  : user.username?.charAt(0) || '?'
            }
            color="white" 
            style={{ backgroundColor: user.is_active ? 'rgb(125, 76, 236)' : '#757575' }}

          />
          
          <View style={styles.userInfo}>
            <Text style={styles.userName}>
              {(user.name && user.surname) 
                ? `${user.name.charAt(0).toUpperCase() + user.name.slice(1)} ${user.surname.charAt(0).toUpperCase() + user.surname.slice(1)}` 
                : user.name 
                  ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
                  : user.username}
            </Text>
            <Text style={styles.userEmail}>{user.email || '-'}</Text>
            
            <View style={styles.chipsContainer}>
              <Chip 
                icon={user.is_active ? "check-circle" : "close-circle"} 
                style={[styles.statusChip, {backgroundColor: user.is_active ? '#E8F5E9' : '#FFEBEE'}]}
              >
                {user.is_active ? 'Aktif' : 'Pasif'}
              </Chip>
              
              <Chip 
                icon={user.is_admin ? "shield-account" : "account"} 
                style={[styles.roleChip, {backgroundColor: user.is_admin ? '#E1F5FE' : '#F3E5F5'}]}
              >
                {user.is_admin ? 'Admin' : 'Kullanıcı'}
              </Chip>
            </View>
          </View>
        </View>
        
        <Divider style={styles.divider} />
        
        <View style={styles.userDetails}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Telefon:</Text>
            <Text style={styles.detailValue}>{user.phone_number || '-'}</Text>
          </View>
          
          
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Kayıt Tarihi:</Text>
            <Text style={styles.detailValue}>{formatDate(user.created_at)}</Text>
          </View>
        </View>
        
        <View style={styles.actionButtons}>
          <Button 
            mode="contained" 
            icon="account-details" 
            onPress={() => navigateToUserDetail(user)}
            style={{backgroundColor: 'rgb(125, 76, 236)'}}
            color="#fff"
          >
            Detaylar
          </Button>
          
          {user.role !== 'admin' && (
  <Button 
    mode="outlined" 
    icon={user.is_active ? "account-off" : "account-check"} 
    onPress={() => confirmToggleStatus(user)}
    style={user.is_active ? styles.deactivateButton : styles.activateButton}
    color="black"
  >
    {user.is_active ? 'Pasif Yap' : 'Aktif Yap'}
  </Button>
)}
        </View>
      </Card.Content>
    </Card>
  );

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Kullanıcı Ara..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      {loading && !refreshing ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Kullanıcılar yükleniyor...</Text>
        </View>
      ) : filteredUsers.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MaterialCommunityIcons name="account-off" size={80} color="#BDBDBD" />
          <Text style={styles.emptyText}>Kullanıcı bulunamadı</Text>
        </View>
      ) : (
        <FlatList
          data={filteredUsers}
          keyExtractor={(item) => item.id.toString()}
          renderItem={renderUserItem}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
        />
      )}

      <Modal
        visible={statusDialogVisible}
        transparent={true}
        onRequestClose={() => setStatusDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
  <View style={styles.errorModalContent}>
    <MaterialCommunityIcons 
      name={userToToggle?.is_active ? "account-off" : "account-check"} 
      size={50} 
      color="rgb(125, 76, 236)"
      style={styles.errorIcon} 
    />

    <Text style={styles.errorModalTitle}>
      {userToToggle?.is_active ? 'Kullanıcıyı Pasif Yap' : 'Kullanıcıyı Aktif Yap'}
    </Text>
    
    <Text style={styles.errorModalText}>
      "{userToToggle?.username || userToToggle?.name}" kullanıcısının durumunu 
      {userToToggle?.is_active ? ' pasif' : ' aktif'} olarak değiştirmek istediğinizden emin misiniz?
      {userToToggle?.is_active ? ' Pasif kullanıcılar uygulamaya giriş yapamayacaktır.' : ''}
    </Text>

    {/* Admin kullanıcıysa butonları gösterme */}
    {userToToggle?.role !== 'admin' ? (
  <View style={styles.errorModalButtonContainer}>
    <Button 
      mode="contained" 
      onPress={handleToggleStatus}
      style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
      icon={userToToggle?.is_active ? "account-off" : "account-check"}
      color="#fff"
    >
      {userToToggle?.is_active ? 'Pasif Yap' : 'Aktif Yap'}
    </Button>
    <Button 
      mode="outlined" 
      onPress={() => setStatusDialogVisible(false)}
      style={styles.modalButton}
      color="rgb(125, 76, 236)"
    >
      İptal
    </Button>
  </View>
) : (
  <Text style={{ textAlign: 'center', color: '#757575', marginTop: 10 }}>
    Admin kullanıcıların durumu değiştirilemez.
  </Text>
)}
  </View>
</View>
      </Modal>

      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  searchbar: {
    marginHorizontal: 16,
    marginVertical: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  listContent: {
    padding: 8,
  },
  card: {
    marginVertical: 6,
    marginHorizontal: 8,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
  userHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userInfo: {
    marginLeft: 16,
    flex: 1,
  },
  userName: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  userEmail: {
    marginTop: 2,
    fontSize: 14,
    color: '#757575',
  },
  chipsContainer: {
    flexDirection: 'row',
    marginTop: 8,
  },
  statusChip: {
    marginRight: 8,
    height: 26,
  },
  roleChip: {
    height: 26,
  },
  divider: {
    marginVertical: 12,
  },
  userDetails: {
    marginBottom: 12,
  },
  // Modal stilleri
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  errorModalContent: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 8,
    padding: 20,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  errorIcon: {
    marginBottom: 15,
  },
  errorModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
    textAlign: 'center',
  },
  errorModalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: '#757575',
  },
  errorModalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    color:'white'
  },
  modalButton: {
    marginHorizontal: 5,
    borderRadius: 4,
    marginTop: 10,
    flex: 1, // Butonun mevcut alanı doldurmasını sağlar
    minWidth: 120, // Minimum genişlik garantisi
  },
  detailRow: {
    flexDirection: 'row',
    marginVertical: 2,
  },
  detailLabel: {
    width: 100,
    color: '#757575',
  },
  detailValue: {
    flex: 1,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  deactivateButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  activateButton: {
    backgroundColor: 'rgb(125, 76, 236)',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  emptyText: {
    fontSize: 18,
    marginTop: 16,
    color: '#757575',
  },
});

export default UserListScreen;
